import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../context/AuthContext';
import { contentService, CreateBeatData } from '../../services/content';
import { storageService } from '../../services/storage';
import { BeatType } from '../../types/content';
import './BeatUpload.css';

const GENRES = [
  'Hip Hop', 'Trap', 'Boom Bap', 'Lo-Fi', 'Drill',
  'Grime', 'Experimental', 'Old School', 'R&B', 'Pop Rap'
];

const WAVE_BARS = 28;

const BeatUpload: React.FC = () => {
  const { authState } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [heroReady, setHeroReady] = useState(false);
  const [formData, setFormData] = useState<Omit<CreateBeatData, 'file_url'>>({
    title: '',
    genre: '',
    bpm: 0,
    tags: [],
    is_public: true,
    verses_count: 10,
    beat_type: 'cypher',
  });
  const [tagInput, setTagInput] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    const t = requestAnimationFrame(() => setHeroReady(true));
    return () => cancelAnimationFrame(t);
  }, []);

  // Only allow producers and producer_rappers to access this component
  if (!authState.user || (authState.user.role !== 'producer' && authState.user.role !== 'producer_rapper')) {
    return (
      <div className="upload-page">
        <Helmet>
          <title>DHH Collab | Upload Beat</title>
        </Helmet>
        <div className="upload-page__bg" aria-hidden>
          <span className="upload-page__blob upload-page__blob--1" />
          <span className="upload-page__blob upload-page__blob--2" />
          <span className="upload-page__grid-pattern" />
        </div>
        <div className="upload-page__inner container-padding">
          <div className="upload-page__card">
            <div className="upload-page__denied">
              <p className="upload-page__denied-title">Access Denied</p>
              <p className="upload-page__denied-text">Only producers can upload beats.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleBeatTypeChange = (nextType: BeatType) => {
    setFormData(prev => ({
      ...prev,
      beat_type: nextType,
      // Max verses only applies to cypher challenges; keep a safe default for collab.
      verses_count: nextType === 'cypher' ? prev.verses_count || 10 : 10,
    }));
    setError(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Stricter file extension check
      const validExtensions = ['.mp3', '.wav'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!validExtensions.includes(fileExtension)) {
        setError('Invalid file type. Only MP3 and WAV files are allowed.');
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleTagAdd = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (formData.tags.length >= 5) {
        setError('Maximum 5 tags allowed');
        return;
      }
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
      setError(null);
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.title.trim()) {
      setError('Title is required.');
      return false;
    }
    if (!formData.genre) {
      setError('Genre is required.');
      return false;
    }
    if (!formData.bpm || formData.bpm <= 0) {
      setError('Please enter a valid BPM.');
      return false;
    }
    if (!selectedFile) {
      setError('Please select a beat file to upload.');
      return false;
    }
    // Clear error if all validations pass
    setError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    // This check is to satisfy TypeScript, as validateForm() already ensures this.
    if (!selectedFile) {
      // This should theoretically never be reached
      setError('An unexpected error occurred. Please select a file again.');
      return;
    }

    setIsUploading(true);
    setSuccess(false);

    try {
      // Upload file to storage
      const fileUrl = await storageService.uploadBeat(selectedFile);

      // Create beat record
      await contentService.createBeat({
        ...formData,
        beat_type: formData.beat_type === 'collaboration' ? 'collaboration' : 'cypher',
        verses_count:
          formData.beat_type === 'collaboration' ? 10 : formData.verses_count || 10,
        is_public: true,
        file_url: fileUrl
      });

      // Reset form
      setFormData({
        title: '',
        genre: '',
        bpm: 0,
        tags: [],
        is_public: true,
        verses_count: 10,
        beat_type: 'cypher',
      });
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSuccess(true);
    } catch (err) {
      console.error('Upload error:', err);
      setError('Error uploading beat. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="upload-page">
      <Helmet>
        <title>DHH Collab | Upload Beat</title>
        <meta
          name="description"
          content="Upload your beats to DHH Collab and share them with rappers across India."
        />
      </Helmet>

      <div className="upload-page__bg" aria-hidden>
        <span className="upload-page__blob upload-page__blob--1" />
        <span className="upload-page__blob upload-page__blob--2" />
        <span className="upload-page__grid-pattern" />
      </div>

      <div className="upload-page__inner container-padding">
        <header className={`upload-page__hero ${heroReady ? 'upload-page__hero--visible' : ''}`}>
          <span className="upload-page__eyebrow">Share your sound</span>
          <h1 className="upload-page__title">Upload Beat</h1>
          <p className="upload-page__subtitle">
            Drop your instrumental for rappers to discover, record on, and collaborate with.
          </p>
          <div className="upload-page__wave" aria-hidden>
            {Array.from({ length: WAVE_BARS }).map((_, i) => {
              const h = 22 + Math.abs(Math.sin(i * 0.55) * 68) + (i % 3) * 6;
              return (
                <span
                  key={i}
                  className="upload-page__wave-bar"
                  style={
                    {
                      '--bar-i': i,
                      '--bar-height': `${h}%`,
                    } as React.CSSProperties
                  }
                />
              );
            })}
          </div>
        </header>

        <div className="upload-page__card">
          <div className="upload-page__disclaimer">
            <strong>Disclaimer:</strong> DHHCollab does not intend to misuse any beat uploaded to this platform or claim any rights to it. All rights to the uploaded beat remain with the original creator.
          </div>

          <form onSubmit={handleSubmit} className="upload-page__form">
            <div className="upload-page__field">
              <label htmlFor="title" className="upload-page__label">
                Title
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="upload-page__input"
                required
              />
            </div>

            <div className="upload-page__field">
              <label htmlFor="genre" className="upload-page__label">
                Genre
              </label>
              <select
                id="genre"
                value={formData.genre}
                onChange={e => setFormData(prev => ({ ...prev, genre: e.target.value }))}
                className="upload-page__select"
                required
              >
                <option value="">Select a genre</option>
                {GENRES.map(genre => (
                  <option key={genre} value={genre}>
                    {genre}
                  </option>
                ))}
              </select>
            </div>

            <div className="upload-page__field">
              <label htmlFor="bpm" className="upload-page__label">
                BPM
              </label>
              <input
                type="number"
                id="bpm"
                min="1"
                max="999"
                value={formData.bpm || ''}
                onChange={e => setFormData(prev => ({ ...prev, bpm: parseInt(e.target.value) || 0 }))}
                className="upload-page__input"
                required
              />
            </div>

            <div className="upload-page__field">
              <span className="upload-page__label" id="beat-type-label">
                Beat purpose
              </span>
              <div className="upload-page__purpose" role="group" aria-labelledby="beat-type-label">
                <label
                  className={`upload-page__purpose-option ${
                    formData.beat_type === 'cypher' ? 'upload-page__purpose-option--active' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="beat_type"
                    value="cypher"
                    checked={formData.beat_type === 'cypher'}
                    onChange={() => handleBeatTypeChange('cypher')}
                  />
                  <span className="upload-page__purpose-title">Cypher challenge</span>
                  <span className="upload-page__purpose-desc">
                    Open floor for multiple rappers. Set a max verse limit.
                  </span>
                </label>
                <label
                  className={`upload-page__purpose-option ${
                    formData.beat_type === 'collaboration' ? 'upload-page__purpose-option--active' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="beat_type"
                    value="collaboration"
                    checked={formData.beat_type === 'collaboration'}
                    onChange={() => handleBeatTypeChange('collaboration')}
                  />
                  <span className="upload-page__purpose-title">Collaboration</span>
                  <span className="upload-page__purpose-desc">
                    Standard collab beat for the feed — not listed on the Cypher page.
                  </span>
                </label>
              </div>
            </div>

            {formData.beat_type === 'cypher' && (
              <div className="upload-page__field">
                <label htmlFor="verses-count" className="upload-page__label">
                  Maximum Verses Allowed
                </label>
                <select
                  id="verses-count"
                  value={formData.verses_count}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, verses_count: parseInt(e.target.value) }))
                  }
                  className="upload-page__select"
                  required
                >
                  <option value={10}>10 (Default)</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
                <p className="upload-page__hint">You can change this later in the beat details page.</p>
              </div>
            )}

            <div className="upload-page__field">
              <label htmlFor="tags" className="upload-page__label">
                Tags (press Enter to add)
              </label>
              <input
                type="text"
                id="tags"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleTagAdd}
                className="upload-page__input"
                placeholder="Add up to 5 tags"
              />
              {formData.tags.length > 0 && (
                <div className="upload-page__tags">
                  {formData.tags.map(tag => (
                    <span key={tag} className="upload-page__tag">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleTagRemove(tag)}
                        className="upload-page__tag-remove"
                      >
                        <span className="sr-only">Remove {tag}</span>
                        <svg stroke="currentColor" fill="none" viewBox="0 0 8 8" aria-hidden>
                          <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="upload-page__field">
              <span className="upload-page__label">Beat File</span>
              <div className="upload-page__dropzone">
                <div className="upload-page__dropzone-inner">
                  <svg
                    className="upload-page__dropzone-icon"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="upload-page__dropzone-text">
                    <label htmlFor="beat-file" className="upload-page__file-label">
                      <span>{selectedFile ? 'Change file' : 'Upload a file'}</span>
                      <input
                        id="beat-file"
                        name="beat-file"
                        type="file"
                        ref={fileInputRef}
                        accept="audio/mpeg,audio/wav"
                        onChange={handleFileChange}
                        required
                      />
                    </label>
                    {' or drag and drop'}
                  </div>
                  <p className="upload-page__dropzone-hint">MP3 or WAV, max 10MB</p>
                </div>
              </div>
              {selectedFile && (
                <p className="upload-page__file-selected">Selected: {selectedFile.name}</p>
              )}
            </div>

            {error && <div className="upload-page__alert upload-page__alert--error">{error}</div>}

            {success && (
              <div className="upload-page__alert upload-page__alert--success">Beat uploaded successfully!</div>
            )}

            <button
              type="submit"
              disabled={isUploading}
              className={`upload-page__submit ${isUploading ? 'upload-page__submit--disabled' : 'upload-page__submit--ready'}`}
            >
              {isUploading ? 'Uploading...' : 'Upload Beat'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BeatUpload;
